import React, { useState, useRef, useEffect } from "react";
import { X, Sparkles, Send, Loader2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useOpsRole } from "../../context/OpsRoleContext";

const SUGGESTIONS = [
  "What should I work on next?",
  "Show me all overdue Stories.",
  "Which branches are close to their allowance?",
  "Find new instructions that haven't been promoted.",
];

const SESSION_ID = `ops-${Math.random().toString(36).slice(2, 10)}`;

export default function OpsAI({ open, onClose }) {
  const { role } = useOpsRole();
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi — I'm your Big Growth AI operations assistant. Ask me what to prioritise, what's overdue, or which branches need attention." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ops/ai", { message: msg, session_id: SESSION_ID, role });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${apiError(e.response?.data?.detail) || "Assistant unavailable."}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} className={`fixed inset-0 z-40 bg-black/30 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} />
      <aside data-testid="ops-ai-panel"
        className={`fixed top-0 right-0 z-50 h-screen w-full max-w-md flex flex-col shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        style={{ background: "#FBFCFA" }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "#E7E8E4", background: "var(--grad-ink)" }}>
          <span className="grid place-items-center w-9 h-9 rounded-xl" style={{ background: "rgba(182,225,79,.18)" }}><Sparkles size={18} color="#B6E14F" strokeWidth={2} /></span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white text-sm">Big Growth AI</div>
            <div className="text-xs" style={{ color: "rgba(255,255,255,.5)" }}>Operations assistant</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="ops-ai-close"><X size={20} /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "text-white" : "text-ink border"}`}
                style={m.role === "user" ? { background: "#2A2D1E" } : { background: "#fff", borderColor: "#E7E8E4" }}
                data-testid={`ai-msg-${m.role}`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start"><div className="rounded-2xl px-4 py-3 border bg-white" style={{ borderColor: "#E7E8E4" }}><Loader2 size={16} className="animate-spin text-ink-3" /></div></div>
          )}
          {messages.length === 1 && (
            <div className="space-y-2 pt-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="ai-suggestion"
                  className="w-full text-left text-sm rounded-xl border px-3.5 py-2.5 text-ink-2 hover:text-ink hover:border-[color:var(--line)] transition-colors bg-white" style={{ borderColor: "#E7E8E4" }}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t" style={{ borderColor: "#E7E8E4" }}>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about your operations..." data-testid="ai-input"
              className="flex-1 rounded-full border bg-white px-4 py-2.5 text-sm text-ink focus-ring" style={{ borderColor: "#E7E8E4" }} />
            <button type="submit" disabled={loading || !input.trim()} data-testid="ai-send"
              className="grid place-items-center w-10 h-10 rounded-full disabled:opacity-40" style={{ background: "var(--grad-green)", color: "#16180F" }}>
              <Send size={16} strokeWidth={2} />
            </button>
          </form>
        </div>
      </aside>
    </>
  );
}
